import { Timestamp } from 'firebase/firestore';

export type LearningFile = {
  name: string;
  url: string;
  storagePath: string;
  contentType: string;
  sizeBytes: number;
};

export type StepCompletionType =
  | 'acknowledgment'
  | 'video'
  | 'document'
  | 'file_upload'
  | 'admin_approval'
  | 'form_submission'
  | 'quiz'
  | 'live_training';

export type QuizQuestion = {
  id: string;
  prompt: string;
};

export const createQuizQuestion = (prompt = ''): QuizQuestion => ({
  id: `q_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`,
  prompt,
});

export const getQuizQuestions = (source?: { quizQuestions?: QuizQuestion[]; quizPrompt?: string } | null): QuizQuestion[] => {
  const listed = (source?.quizQuestions || [])
    .filter(question => question?.prompt?.trim())
    .map(question => ({ id: question.id || createQuizQuestion().id, prompt: question.prompt.trim() }));
  if (listed.length) return listed;
  const legacy = source?.quizPrompt?.trim();
  return legacy ? [{ id: 'legacy', prompt: legacy }] : [];
};

export const quizQuestionsForEditor = (source?: { quizQuestions?: QuizQuestion[]; quizPrompt?: string } | null): QuizQuestion[] => {
  const listed = source?.quizQuestions || [];
  if (listed.length) return listed.map(question => ({ id: question.id || createQuizQuestion().id, prompt: question.prompt || '' }));
  return [createQuizQuestion(source?.quizPrompt || '')];
};

export const formatQuizSubmission = (questions: QuizQuestion[], answers: Record<string, string>): string =>
  questions.map((question, index) => {
    const answer = (answers[question.id] || '').trim();
    return questions.length === 1 ? answer : `Q${index + 1}. ${question.prompt}\n${answer}`;
  }).join('\n\n');

export type CertificationStep = {
  id: string;
  certificationId: string;
  title: string;
  description: string;
  writtenContent?: string;
  completionType: StepCompletionType;
  video?: LearningFile | null;
  documents?: LearningFile[];
  quizPrompt?: string;
  quizQuestions?: QuizQuestion[];
  linkUrl?: string;
  linkLabel?: string;
  /** Connects a certification step to its built-in hiring action. */
  onboardingTaskId?: string;
  /** Optional onboarding steps do not block full platform access. */
  required?: boolean;
  order: number;
  published: boolean;
  createdAt?: Timestamp | Date | string;
  updatedAt?: Timestamp | Date | string;
};

export type CertificationPathKind = 'step' | 'module' | 'training';

export type CertificationPathItem = {
  kind: CertificationPathKind;
  id: string;
};

export type Certification = {
  id: string;
  title: string;
  description: string;
  kind?: 'standard' | 'onboarding';
  order: number;
  published: boolean;
  moduleIds?: string[];
  trainingIds?: string[];
  /** Mixed teacher-facing order for steps, modules, and direct lessons. */
  pathItems?: CertificationPathItem[];
  steps: CertificationStep[];
  createdAt?: Timestamp | Date | string;
  updatedAt?: Timestamp | Date | string;
};

export const pathItemKey = (item: CertificationPathItem) => `${item.kind}|${item.id}`;

export const parsePathItemKey = (value: string): CertificationPathItem | null => {
  const divider = value.indexOf('|');
  if (divider <= 0) return null;
  const kind = value.slice(0, divider);
  const id = value.slice(divider + 1);
  if ((kind !== 'step' && kind !== 'module' && kind !== 'training') || !id) return null;
  return { kind, id };
};

export const defaultCertificationPath = (certification: Pick<Certification, 'steps' | 'moduleIds' | 'trainingIds'>): CertificationPathItem[] => [
  ...[...(certification.steps || [])].sort((a, b) => a.order - b.order).map(step => ({ kind: 'step' as const, id: step.id })),
  ...(certification.moduleIds || []).map(id => ({ kind: 'module' as const, id })),
  ...(certification.trainingIds || []).map(id => ({ kind: 'training' as const, id })),
];

export const resolveCertificationPath = (certification: Certification): CertificationPathItem[] => {
  const fallback = defaultCertificationPath(certification);
  const stored = certification.pathItems || [];
  if (!stored.length) return fallback;
  const allowed = new Set(fallback.map(pathItemKey));
  const seen = new Set<string>();
  const resolved: CertificationPathItem[] = [];
  for (const item of stored) {
    const key = pathItemKey(item);
    if (!allowed.has(key) || seen.has(key)) continue;
    seen.add(key);
    resolved.push(item);
  }
  for (const item of fallback) {
    const key = pathItemKey(item);
    if (seen.has(key)) continue;
    seen.add(key);
    resolved.push(item);
  }
  return resolved;
};

export const pathIdsOfKind = (path: CertificationPathItem[], kind: CertificationPathKind) =>
  path.filter(item => item.kind === kind).map(item => item.id);

export type TrainingAudience = 'teachers' | 'operations' | 'admins' | 'parents' | 'public';

/** A reusable, independently addressable piece of learning content. */
export type LearningTraining = {
  id: string;
  title: string;
  description: string;
  writtenContent?: string;
  learningObjective?: string;
  durationMinutes?: number;
  /** Legacy records may still contain seconds; new writes use durationMinutes. */
  durationSeconds?: number;
  thumbnail?: LearningFile | null;
  video?: LearningFile | null;
  documents?: LearningFile[];
  linkUrl?: string;
  linkLabel?: string;
  tags: string[];
  searchKeywords: string[];
  audience: TrainingAudience[];
  completionType: StepCompletionType;
  quizPrompt?: string;
  quizQuestions?: QuizQuestion[];
  published: boolean;
  communityEducation: boolean;
  createdAt?: Timestamp | Date | string;
  updatedAt?: Timestamp | Date | string;
};

export const getTrainingDurationMinutes = (training: Pick<LearningTraining, 'durationMinutes' | 'durationSeconds'>) =>
  training.durationMinutes ?? (training.durationSeconds != null ? training.durationSeconds / 60 : 0);

export const formatTrainingDuration = (training: Pick<LearningTraining, 'durationMinutes' | 'durationSeconds'>) => {
  const minutes = getTrainingDurationMinutes(training);
  return `${Number(minutes.toFixed(2))} min`;
};

export const formatModuleDuration = (trainings: Pick<LearningTraining, 'durationMinutes' | 'durationSeconds'>[]) => {
  const total = trainings.reduce((sum, training) => sum + getTrainingDurationMinutes(training), 0);
  return total > 0 ? `~${Math.max(1, Math.round(total))} min` : '';
};

/** An ordered, reusable group of trainings. */
export type LearningModule = {
  id: string;
  title: string;
  description: string;
  trainingIds: string[];
  published: boolean;
  createdAt?: Timestamp | Date | string;
  updatedAt?: Timestamp | Date | string;
};

export type CommunityTrainingStatus = 'pending' | 'approved' | 'rejected';

export type CommunityTraining = {
  id: string;
  title: string;
  description: string;
  writtenContent?: string;
  video?: LearningFile | null;
  documents?: LearningFile[];
  authorId: string;
  authorName: string;
  status: CommunityTrainingStatus;
  moderationNote?: string;
  createdAt?: Timestamp | Date | string;
  updatedAt?: Timestamp | Date | string;
};

export type TrainingComment = {
  id: string;
  authorId: string;
  authorName: string;
  text: string;
  createdAt?: Timestamp | Date | string;
};

export type TrainingCommentPath = {
  collection: 'learningTrainings' | 'communityTrainings';
  id: string;
};

export const COMPLETION_TYPE_LABELS: Record<StepCompletionType, string> = {
  acknowledgment: 'Written acknowledgment',
  video: 'Watch video',
  document: 'Review document',
  file_upload: 'Upload a file',
  admin_approval: 'Admin approval',
  form_submission: 'Submit a form',
  quiz: 'Quiz',
  live_training: 'Live observation or practice',
};
